<template>
  <header v-if="!fromDetailTemplate">
    <h3>Configuración general de la secuencia</h3>
    <p>Completa los campos requeridos</p>
  </header>

  <CustomScrollbar height="calc(100% - 120px)" style="padding-right: 20px">
    <a-form ref="formRef" layout="vertical" :model="formState">
      <h4>Datos básicos</h4>

      <a-form-item label="Nombre de la secuencia" name="name" required>
        <a-input
          v-model:value="formState.name"
          placeholder="Escribir nombre de la secuencia"
          :disabled="disabled"
        />
      </a-form-item>

      <a-form-item label="Descripción de la secuencia" name="description">
        <a-textarea
          :disabled="disabled"
          v-model:value="formState.description"
          placeholder="Escribe aquí..."
          :autosize="{ minRows: 2, maxRows: 2 }"
          :maxlength="100"
          showCount
        />
      </a-form-item>

      <a-divider class="divider" />

      <h4>Configuración de la secuencia</h4>

      <EntityPicker
        :value="formState.entity"
        validateWithPortfolios
        @update:value="handleUpdateEntity"
        withName
        defaultBehavior
        :showAll="false"
        label="Empresa"
        :include-item="props.sequence?.portfolio?.entity ?? undefined"
        required
        :disabled="disabled || Boolean(props.sequence)"
        toSequence
        triggerNode
      />

      <CampaignPicker
        ref="campaignPickerRef"
        style="margin-bottom: 15px !important"
        :value="formState.portfolio"
        @update:value="handleUpdatePortfolio"
        withName
        defaultBehavior
        validateSequence
        :entity="formState.entity"
        :showAll="false"
        :include-item="props.sequence?.portfolio ?? undefined"
        label="Campaña"
        required
        :disabled="disabled || Boolean(props.sequence)"
        triggerNode
      />

      <a-alert
        v-if="!fromDetailTemplate"
        :message="
          validateItemSequence() == 1
            ? `Esta campaña esta siendo utilizada en otra secuencia activa. Recuerda que solo se puede tener una secuencia activa por campaña.`
            : validateItemSequence() == 2
            ? 'Esta campaña esta siendo utilizada en otra secuencia inactiva. Recuerda que solo se puede tener una secuencia activa por campaña.'
            : 'Recuerda que solo se puede tener una secuencia activa por campaña.'
        "
        type="warning"
        style="margin-bottom: 20px"
        show-icon
      />

      <a-form-item label="Orden de contacto" name="distribution_type" required>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.distribution_type"
          :disabled="disabled"
        >
          <a-select-option :value="SequenceDistributionType.FIFO">
            Primero en entrar, primero en salir (FIFO)
          </a-select-option>
          <a-select-option :value="SequenceDistributionType.LIFO">
            Último en entrar, primero en salir (LIFO)
          </a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item name="priority_active" required>
        <template #label>
          Contactar por priorización
          <a-tooltip title="Los valores menores serán considerados de mayor priorización.">
            <InfoCircleOutlined style="margin-left: 5px" />
          </a-tooltip>
        </template>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.priority_active"
          :disabled="disabled"
        >
          <a-select-option :value="false"> No </a-select-option>
          <a-select-option :value="true"> Sí </a-select-option>
        </a-select>
      </a-form-item>

      <a-alert
        v-if="!fromDetailTemplate"
        message="Recuerda que los nuevos contactos serán añadidos a una secuencia por campaña."
        type="info"
        style="margin-bottom: 20px"
        show-icon
      />
    </a-form>
  </CustomScrollbar>
</template>

<script setup lang="ts">
import { createVNode, reactive, ref, watch } from "vue";
// import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { isEqual, cloneDeep } from "lodash";
import { FormInstance, Modal } from "ant-design-vue";

import { Sequence, SequenceDistributionType } from "@/@types/flows/sequences";
import { ConfigStepDto } from "@/@types/flows/sequences/dto";

import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { QuestionCircleOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";

const props = defineProps<{
  fromDetailTemplate?: boolean;
  sequence?: Sequence;
  disabled?: boolean;
  distributionType?: number;
}>();

const emit = defineEmits<{
  toggleButton: [disabled: boolean];
}>();

const formRef = ref<FormInstance>();
const formState = reactive<ConfigStepDto & { entity: number | undefined }>({
  name: props.sequence?.name || "",
  description: props.sequence?.description || "",
  distribution_type: props.distributionType || props.sequence?.distribution_type || SequenceDistributionType.FIFO,
  // distribution_type: props.distributionType || SequenceDistributionType.FIFO,
  portfolio: props.sequence?.portfolio.id || undefined,
  priority_active: props.sequence?.priority_active || false,
  entity: props.sequence?.portfolio.entity.id || undefined,
});

const campaignPickerRef = ref();
const portfolioItem = ref<{ id: number; sequences_data: [] } | null>(null);

const validateItemSequence = () => {
  if (portfolioItem.value) {
    const sequenceActive = portfolioItem.value?.sequences_data.find((el: any) => el.status == 1);
    const sequenceInactive = portfolioItem.value?.sequences_data.find((el: any) => el.status != 1);
    // console.log(sequenceActive, "sequence active");
    if (sequenceActive) {
      return 1; // secuencia activa
    } else if (sequenceInactive) {
      return 2; // no hay secuencia activa con esa campaña
    }
  }
  return 0;
};

const handleUpdateEntity = (newEntity: number) => {
  if (formState.entity != newEntity && formState.entity && props.sequence) {
    Modal.confirm({
      centered: true,
      icon: createVNode(QuestionCircleOutlined),
      title: "¿Estás seguro de cambiar tu empresa?",
      content:
        "Recuerda que los datos que se están procesando en la empresa y campaña anterior pueden verse afectados o interrumpidos.",
      okText: "Aceptar",
      onOk: () => {
        formState.entity = newEntity;
        formState.portfolio = undefined;
      },
      onCancel: () => {
        return;
      },
    });
  } else {
    formState.entity = newEntity;
    formState.portfolio = undefined;
  }
};

const handleUpdatePortfolio = (newPortfolio: number) => {
  if (formState.portfolio != newPortfolio && formState.portfolio && props.sequence && newPortfolio) {
    Modal.confirm({
      centered: true,
      icon: createVNode(QuestionCircleOutlined),
      title: "¿Estás seguro de cambiar tu campaña?",
      content:
        "Recuerda que los datos que se están procesando en la campaña anterior pueden verse afectados o interrumpidos.",
      okText: "Aceptar",
      onOk: () => {
        formState.portfolio = newPortfolio;
      },
      onCancel: () => {
        return;
      },
    });
  } else {
    formState.portfolio = newPortfolio;
  }

  portfolioItem.value = campaignPickerRef.value.portfolios.find(
    (el: any) => el.id == formState.portfolio
  );

  // if (!props.fromDetailTemplate) formState.portfolio = newPortfolio;
};

const comparisonForm = cloneDeep(formState);

watch(
  formState,
  async () => {
    console.log(formState, "formState");
    if (formState.name && formState.entity && formState.portfolio != undefined) {
      const isDisabled = isEqual(formState, comparisonForm);
      console.log(isDisabled, "is disabled");
      await formRef.value
        ?.validate()
        .then(() => {
          emit("toggleButton", isDisabled);
        })
        .catch((error) => {
          console.log("invalid validation", error);
          emit("toggleButton", true);
        });
    } else {
      console.log("invalid validation 2");
      emit("toggleButton", true);
    }
  },
  { immediate: true, deep: true }
);

const getFormState = (): ConfigStepDto => formState;

defineExpose({ getFormState, formRef });
</script>

<style scoped lang="sass">
h3
  color: $gray-9
  font-size: 20px
  margin-bottom: 16px

h4
  color: $gray-9
  font-size: 14px
  margin-bottom: 16px
  font-weight: 600

.divider
  background-color: $gray-6

header
  width: 100%
  text-align: center
  margin-bottom: 25px

  p
    color: $text-secondary
    margin: 0
    font-size: 16px
</style>
